import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Activity, Moon, Flower2, Shield, Check } from 'lucide-react';
import Footer from '@/components/shared/Footer';

export default function ModulesPage() {
  const { language } = useLanguage();
  const nav = useNavigate();
  const de = language === 'de';

  const modules = [
    {
      icon: Activity, color: 'purple', title: 'Hashimoto',
      desc: de ? 'Schilddrüsenunterfunktion verstehen und optimieren – mit TSH-Tracking, Selen-Monitoring und personalisierten Ernährungstipps.' : 'Understand and optimize thyroid function – with TSH tracking, selenium monitoring and personalized nutrition tips.',
      features: de ? ['TSH / fT3 / fT4 Trend-Grafiken', 'Selen & Omega-3 Monitoring', 'Symptom-Heatmap & Mustererkennung', 'Medikamenten-Timer (Levothyroxin)', 'Warnungen bei kritischen Werten'] : ['TSH / fT3 / fT4 trend charts', 'Selenium & omega-3 monitoring', 'Symptom heatmap & pattern recognition', 'Medication timer (Levothyroxine)', 'Warnings for critical values'],
    },
    {
      icon: Moon, color: 'orange', title: de ? 'Peri-/Menopause' : 'Peri-/Menopause',
      desc: de ? 'Hitzewallungen, Schlafstörungen, Stimmung – tracke deine Symptome und entdecke, welche Trigger sie auslösen.' : 'Hot flashes, sleep issues, mood – track your symptoms and discover which triggers cause them.',
      features: de ? ['Hitzewallungen & Nachtschweiss zählen', 'Trigger-Korrelationsanalyse', 'Symptom-Heatmap (28 Tage)', 'Schlaf, Stimmung, Brain Fog Tracking', 'Trend-Diagramme über Zeit'] : ['Hot flash & night sweat counter', 'Trigger correlation analysis', 'Symptom heatmap (28 days)', 'Sleep, mood, brain fog tracking', 'Trend charts over time'],
    },
    {
      icon: Flower2, color: 'pink', title: de ? 'Zyklus' : 'Cycle',
      desc: de ? 'Verstehe deinen Zyklus und optimiere Ernährung & Training nach deiner aktuellen Phase.' : 'Understand your cycle and optimize nutrition & training based on your current phase.',
      features: de ? ['Zyklustag & Phasen-Erkennung', 'Symptom-Tracking pro Phase', 'Temperatur-Logging', 'Phasenbasierte Ernährungstipps', 'Heisshunger-Prävention in Lutealphase'] : ['Cycle day & phase detection', 'Symptom tracking per phase', 'Temperature logging', 'Phase-based nutrition tips', 'Craving prevention in luteal phase'],
    },
  ];

  return (
    <div className="min-h-screen bg-bone" data-testid="modules-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground mb-8 font-body text-sm transition-colors" data-testid="modules-back">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>

        <div className="text-center mb-12">
          <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">
            {de ? 'SPEZIALBEREICHE' : 'SPECIALIST AREAS'}
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">
            {de ? 'Für deine besonderen Themen' : 'For your special topics'}
          </h1>
          <p className="font-body text-base text-foreground/50 mt-3 max-w-xl mx-auto">
            {de ? 'Neben Ernährung & Glow bietet belosophie spezialisierte Module für Frauen mit Hashimoto, in der Peri-/Menopause oder mit Zyklus-Themen.' : 'In addition to Nutrition & Glow, belosophie offers specialized modules for women with Hashimoto, in peri-/menopause or with cycle topics.'}
          </p>
        </div>

        <div className="space-y-6">
          {modules.map((m, i) => (
            <Card key={i} className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 hover:shadow-md transition-all" data-testid={`specialist-module-${i}`}>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl bg-${m.color}-100 flex items-center justify-center`}>
                      <m.icon size={20} className={`text-${m.color}-500`} />
                    </div>
                    <h2 className="font-display text-2xl font-medium">{m.title}</h2>
                  </div>
                  <p className="font-body text-sm text-foreground/60 leading-relaxed mb-4">{m.desc}</p>
                  <ul className="space-y-2">
                    {m.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 font-body text-sm text-foreground/70">
                        <Check size={14} className={`text-${m.color}-500 shrink-0`} /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-10 p-4 rounded-xl bg-warmgray/50 border border-border/30">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-foreground/30 shrink-0 mt-0.5" />
            <p className="font-body text-xs text-foreground/40 leading-relaxed">
              {de
                ? 'Diese Module unterstützen bei Ernährung und Lebensstil. Sie ersetzen keine ärztliche Diagnose oder Therapie. Laborwerte dienen der persönlichen Dokumentation. Konsultiere bei gesundheitlichen Fragen immer deine Ärztin oder deinen Endokrinologen.'
                : 'These modules support nutrition and lifestyle. They do not replace medical diagnosis or therapy. Lab values serve personal documentation. Always consult your doctor or endocrinologist for health questions.'}
            </p>
          </div>
        </div>

        <div className="text-center mt-10">
          <Button onClick={() => nav('/auth?mode=register')} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-8 py-5 text-base font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="modules-cta">
            {de ? 'Kostenlos starten' : 'Start free'} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
